import Spinner from '@components/common/spinner'
import CountDown from '@components/quizzes/arena/count-down'
import Layout from '@components/quizzes/arena/layout'
import UsernameForm from '@components/quizzes/arena/username-form'
import ProblemsView from '@components/quizzes/arena/problems/problems-view'
import { useSessionContext } from '@contexts/session'
import {
  QUIZZES_SOUND_PATH,
  QUIZ_ARENA_STATE,
  QUIZ_USERNAME_LOCAL_STORAGE_KEY,
} from '@lib/constants/quizzes'
import {
  CountDownSoundsType,
  ProblemSubmissionResponseType,
  QuizArenaStateType,
  QuizLinkedEntityType,
  QuizProblemType,
} from '@lib/types/quizzes'
import { getFromLocalStorage } from '@lib/utils/local-storage'
import { useEffect, useState } from 'react'
import { CClient, staticFile } from '@common/utils'
import Result from '@components/quizzes/arena/result/result'
import { GetServerSideProps } from 'next'
import { CompetitionEventDetailsProps } from '@lib/types/competitions'
import { parseCookies } from 'nookies'
import { userFetcher } from 'data-fetchers/common/user-fetcher'
import { ROUTES } from '@common/enums'
import { getServerSideRedirectResult } from '@lib/utils/common'

/*
Flow:
1. [USERNAME_INPUT] Username input (For anon users)
2. [LOADING] Loading sound and kept for UX purposes
3. [SHOW_PROBLEMS] show the problem view
4. [COMPLETE] show quiz summary view
*/

type Props = {
  quizEventDetails: CompetitionEventDetailsProps<QuizLinkedEntityType>
  attemptedProblems: QuizProblemType[]
  totalPointsObtained: number
}

const Arena = ({
  quizEventDetails: { phases, title, slug },
  attemptedProblems: attemptedProblemsProps,
  totalPointsObtained,
}: Props) => {
  const {
    COMPLETE,
    COUNT_DOWN,
    FETCH_PROBLEMS,
    SHOW_PROBLEMS,
    LOADING,
    USERNAME_INPUT,
  } = QUIZ_ARENA_STATE

  const {
    remainingProblems,
    id: quizId,
    totalProblems,
    problemIds,
    playCount,
    isLessonQuiz,
  } = phases[0].linkedEntity

  const { loggedIn } = useSessionContext()
  const [arenaState, setArenaState] = useState<QuizArenaStateType>(LOADING)
  const [countDownSounds, setCountDownSounds] = useState<CountDownSoundsType>({
    numeric: null,
    go: null,
  })
  const [currentPoints, setCurrentPoints] = useState(totalPointsObtained)
  const [attemptedProblems, setAttemptedProblems] = useState<QuizProblemType[]>(
    attemptedProblemsProps
  )

  useEffect(() => {
    // Loading audio files before hand so they are availabe for the countdown screen
    setCountDownSounds({
      go: new Audio(staticFile(`${QUIZZES_SOUND_PATH}count-down-go.mp3`)),
      numeric: new Audio(
        staticFile(`${QUIZZES_SOUND_PATH}count-down-numeric.mp3`)
      ),
    })

    if (remainingProblems === 0) {
      setArenaState(COMPLETE)
      return
    }

    const timeout = setTimeout(() => {
      if (loggedIn) {
        setArenaState(COUNT_DOWN)
        return
      }
      getFromLocalStorage(QUIZ_USERNAME_LOCAL_STORAGE_KEY)
        ? setArenaState(COUNT_DOWN)
        : setArenaState(USERNAME_INPUT)
    }, 2000)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <Layout>
      {arenaState === USERNAME_INPUT && (
        <UsernameForm setNextStep={() => setArenaState(COUNT_DOWN)} />
      )}
      {/* TODO: Use a custom loader */}
      {(arenaState === FETCH_PROBLEMS || arenaState === LOADING) && (
        <div className="flex items-center justify-center h-full">
          <Spinner classes="w-10 text-white" />
        </div>
      )}
      {arenaState === COUNT_DOWN && (
        <CountDown
          setNextStep={() => setArenaState(SHOW_PROBLEMS)}
          goCountDownSound={countDownSounds.go}
          numericCountDownSound={countDownSounds.numeric}
        />
      )}
      {arenaState === SHOW_PROBLEMS && (
        <ProblemsView
          currentProblemIndex={attemptedProblems.length}
          quizId={quizId}
          quizSlug={slug}
          quizTitle={title}
          totalProblems={totalProblems}
          currentPoints={currentPoints}
          setCurrentPoints={setCurrentPoints}
          attemptedProblems={attemptedProblems}
          setAttemptedProblems={setAttemptedProblems}
          setArenaState={setArenaState}
          problemIds={problemIds}
          isLessonQuiz={isLessonQuiz}
        />
      )}
      {arenaState === COMPLETE && (
        <Result
          points={currentPoints}
          problems={attemptedProblems}
          quizTitle={title}
          quizId={quizId}
          slug={slug}
          playCount={playCount}
          isLessonQuiz={isLessonQuiz}
        />
      )}
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const token = parseCookies(ctx).token
  const quizSlug = ctx.params?.quizName
  const response = await CClient(`/api/v1/competitions/${quizSlug}/`, {
    method: 'GET',
    headers: {
      cookie: ctx.req.headers.cookie,
      Authorization: token ? `Token ${token}` : null,
    },
  })

  if (response.status === 404) return { notFound: true }

  if (!response.ok) {
    return getServerSideRedirectResult(ctx, `/${response.status}`)
  }

  let attemptedProblems: QuizProblemType[] = []
  let totalPointsObtained = 0

  const quizEventDetails: CompetitionEventDetailsProps<QuizLinkedEntityType> = await response.json()

  /** Redirect user to quiz list page if current quiz is a lesson quiz and the user is not a paid user */
  if (quizEventDetails?.phases?.[0]?.linkedEntity?.isLessonQuiz) {
    const userData = await userFetcher('/api/v1/users/me/', token, ctx)
    if (!userData.loggedIn || !userData.isPaidUser)
      return getServerSideRedirectResult(ctx, ROUTES.QUIZZES_LIST)
  }

  const quizEntity = quizEventDetails.phases[0].linkedEntity
  if (quizEntity.totalProblems !== quizEntity.remainingProblems) {
    const problemSubmissionResponse = await CClient(
      `/api/v1/competitions/${quizSlug}/quiz-submissions/?quiz=${quizEntity.id}&page_size=50`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          cookie: ctx.req.headers.cookie,
          Authorization: token ? `Token ${token}` : null,
        },
      }
    )

    if (!problemSubmissionResponse.ok)
      return getServerSideRedirectResult(
        ctx,
        `/${problemSubmissionResponse.status}`
      )

    const data = await problemSubmissionResponse.json()

    // All the submissions for current quiz participation
    const submissions: ProblemSubmissionResponseType[] = data.results

    // If a problem is submitted hence it is attempted.
    attemptedProblems = submissions.map(
      ({ problem, solution, submittedChoices, result }) => {
        const submittedChoiceIds = submittedChoices.map((choice) => choice.id)
        const isCorrect = result !== 'Wrong Answer'
        const correctChoicesIds = isCorrect
          ? submittedChoiceIds
          : solution.map((choice) => choice.id)

        return {
          ...problem,
          correctChoices: correctChoicesIds,
          submittedChoices: submittedChoiceIds,
          isCorrect,
          isAnswerValidated: true,
        }
      }
    )

    submissions.forEach(
      (submission) => (totalPointsObtained += submission.pointsObtained)
    )
  }
  return { props: { quizEventDetails, attemptedProblems, totalPointsObtained } }
}

export default Arena
